import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { GridsterConfig, GridsterItem } from "angular-gridster2";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { ReportChartEditFormDialog, ReportChartEditFormDialogData } from "../../report-dialogs/report-chart-edit-form/report-chart-edit-form.dialog";
import { Dialog } from "@core/material";
import { FormControl, FormGroup } from "@angular/forms";
import "@sgratzl/chartjs-chart-boxplot/build/index.umd.js";
import 'chartjs-adapter-moment';
import { BoxAndWiskers, BoxPlotController } from '@sgratzl/chartjs-chart-boxplot';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ChartCompiledData, MeasureChartComponentData, ReportComponentType, ResolvedTypography } from '@core/data/types/advanced-report';
import { editModeCheck } from '../../report-view/report-state/advanced-report.state';

@Component({
    selector: 'pv-report-chart',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    templateUrl: 'report-chart-component.html',
    styleUrls: ['report-chart-component.scss']
})
export class ReportChartComponent implements OnDestroy, OnChanges {

    @Input()
    set compiledData(data: ChartCompiledData) {
        if (this._compiledData$.getValue() !== data) this._compiledData$.next(data);
    }

    @Input()
    chartControl: FormControl;

    @Input()
    typography: ResolvedTypography;

    @Input()
    configControl: FormControl;

    @Input()
    item: GridsterItem;

    @Input()
    editMode: boolean

    @Input()
    optionKey: string;

    @Input()
    reportKey: string;

    @Input()
    ownerOrgKey: string;

    @Input()
    gridsterConfig: GridsterConfig;

    @Input()
    configFormGroup: FormGroup;

    @Output()
    delete = new EventEmitter<boolean>();

    @Output()
    duplicate = new EventEmitter<ReportComponentType>();

    @Output()
    editTypography = new EventEmitter<void>();

    layerIndex = 1;

    get isUnset(): boolean {
        const v = this.chartControl?.value as MeasureChartComponentData;
        return !v || !v.type || !v.measure;
    }

    private _compiledData$ = new BehaviorSubject<ChartCompiledData>(null);
    private _destroy$ = new Subject();

    constructor(
        private _dialog: Dialog,
    ) {}

    ngOnChanges() {
        this.layerIndex = this.item?.layerIndex ? this.item.layerIndex : 1;
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    moveToTop() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.maxLayerIndex,
        });
    }

    moveForward() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.baseLayerIndex,
        });
    }

    moveDown() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.baseLayerIndex,
        });
    }

    moveToBottom() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.maxLayerIndex,
        });
    }

    getChartValue() {
        return this._compiledData$.getValue();
    }

    edit() {
        if (!editModeCheck(this.editMode)) return;

        const data: ReportChartEditFormDialogData = {
            reportKey: this.reportKey,
            ownerOrgKey: this.ownerOrgKey,
            optionKey: this.optionKey,
            data: this.chartControl.value,
        };

        this._dialog.open(ReportChartEditFormDialog, {
            data,
            panelClass: 'reporting-theme',
            width: '720px',
            maxWidth: '95vw',
            autoFocus: false,
        }).afterClosed()
        .subscribe((result) => {
            if (!result?.status) return;

            this.chartControl.setValue(result?.data);
            this.chartControl.markAsDirty();
        })
    }

    remove() {
        this._dialog.confirm(
            'Delete Component',
            `Are you sure you want to delete this component?`,
            'Delete',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.delete.emit(true);
        })
    }

    makeDuplicate() {
        this.duplicate.emit(ReportComponentType.CHART);
    }
}